import React from 'react';
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { ACM } from '../../../constants/UserRoles';

const ITEM_HEIGHT = 70;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function StudentPatientSelector({
  typeOfUser,
  selectedStudent,
  selectedPatient,
  studentsList,
  patientData,
  onSelectStudent,
  onSelectPatient,
}) {
  const isTeacher = ACM[typeOfUser]?.isTeacher;
  const isDoctor = ACM[typeOfUser]?.isDoctor;
  const isStudent = ACM[typeOfUser]?.isStudent;
  const isPatient = ACM[typeOfUser]?.isPatient;

  // Don't show selector for students/patients - they only see their own goals
  if (isStudent || isPatient) {
    return null;
  }

  if (isTeacher) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography
          variant='body2'
          sx={{
            fontWeight: 500,
            color: 'text.primary',
            fontSize: '0.875rem',
            whiteSpace: 'nowrap',
          }}
        >
          Select Student
        </Typography>
        <FormControl sx={{ minWidth: 150 }}>
          <Select
            MenuProps={MenuProps}
            sx={{
              width: 215,
              border: '1px solid var(--clr-mindly-purple)',
              height: '40px',
              borderRadius: '10px',
              boxShadow: '0px 0px 0px 0px var(--clr-mindly-purple)',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
            labelId='demo-simple-select-label'
            id='demo-simple-select'
            value={selectedStudent}
            inputProps={{ 'aria-label': 'Without label' }}
            onChange={(event) => onSelectStudent(event.target.value)}
          >
            {(Array.isArray(studentsList) ? [...studentsList] : [])
              .sort((a, b) => {
                const nameA = (a?.name || a?.displayName || '').toLowerCase();
                const nameB = (b?.name || b?.displayName || '').toLowerCase();
                return nameA.localeCompare(nameB);
              })
              .map((student) => (
                <MenuItem key={student.id} value={student.id}>
                  <Tooltip title={student?.name || student?.displayName}>
                    <span>
                      {student?.name || student?.displayName || 'Unknown Student'}
                    </span>
                  </Tooltip>
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  if (isDoctor) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography
          variant='body2'
          sx={{
            fontWeight: 500,
            color: 'text.primary',
            fontSize: '0.875rem',
            whiteSpace: 'nowrap',
          }}
        >
          Select Patient
        </Typography>
        <FormControl sx={{ minWidth: 150 }}>
          <Select
            MenuProps={MenuProps}
            sx={{
              width: 215,
              border: '1px solid var(--clr-mindly-purple)',
              height: '40px',
              borderRadius: '10px',
              boxShadow: '0px 0px 0px 0px var(--clr-mindly-purple)',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
            labelId='demo-simple-select-label'
            id='demo-simple-select'
            value={selectedPatient}
            inputProps={{ 'aria-label': 'Without label' }}
            onChange={(event) => onSelectPatient(event.target.value)}
          >
            <MenuItem value='All'>All Patients</MenuItem>
            {(Array.isArray(patientData) ? [...patientData] : [])
              .sort((a, b) => {
                const nameA = (a?.name || a?.displayName || '').toLowerCase();
                const nameB = (b?.name || b?.displayName || '').toLowerCase();
                return nameA.localeCompare(nameB);
              })
              .map((patient) => (
                <MenuItem key={patient.id} value={patient.id}>
                  <Tooltip title={patient?.name || patient?.displayName}>
                    <span>
                      {patient?.name || patient?.displayName || 'Unknown Patient'}
                    </span>
                  </Tooltip>
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  return null;
}
